import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Menu, PanelRightOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getMyRole } from "@/lib/dr-sara.functions";
import { supabase } from "@/integrations/supabase/client";
import logoWide from "@/assets/drsara-logo-maroon.png.asset.json";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { NavRail } from "@/components/admin/nav-rail";
import { NavSubPanel } from "@/components/admin/nav-subpanel";
import { resolveActive, visibleSections } from "@/components/admin/nav-sections";

const roleQuery = () =>
  queryOptions({
    queryKey: ["me", "role"],
    queryFn: () => getMyRole(),
  });

export const Route = createFileRoute("/_authenticated/admin")({
  loader: async ({ context }) => {
    const role = await context.queryClient.ensureQueryData(roleQuery());
    if (!role.isStaff) {
      throw redirect({ to: "/portal", search: {} });
    }
    return { role };
  },
  head: () => ({
    meta: [
      { title: "لوحة التحكم — د. سارة النحّاس" },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  notFoundComponent: RouteNotFound,
  errorComponent: RouteError,
  component: AdminShell,
});

const PANEL_KEY = "admin:nav:subpanel-collapsed";

function AdminShell() {
  const { data: role } = useSuspenseQuery(roleQuery());
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const sections = useMemo(() => visibleSections(role.isAdmin), [role.isAdmin]);
  const { sectionId, activeTo } = useMemo(
    () => resolveActive(pathname, role.isAdmin),
    [pathname, role.isAdmin],
  );
  const activeSection = useMemo(
    () => sections.find((s) => s.id === sectionId) ?? sections[0] ?? null,
    [sections, sectionId],
  );

  const [panelCollapsed, setPanelCollapsed] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PANEL_KEY);
      if (raw) setPanelCollapsed(raw === "1");
    } catch {}
  }, []);
  const togglePanel = () =>
    setPanelCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PANEL_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });

  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  async function handleSignOut() {
    const { performSignOut } = await import("@/lib/auth/sign-out");
    await performSignOut(queryClient);
    navigate({ to: "/auth", replace: true });
  }

  const railProps = {
    sections,
    activeSectionId: activeSection?.id ?? null,
    onSignOut: handleSignOut,
    logoUrl: logoWide.url,
  };

  return (
    <div className="min-h-dvh bg-background bg-grain">
      <div className="mx-auto flex min-h-dvh max-w-[1500px]">
        {/* Desktop sidebar: panel + rail (RTL: rail is rightmost) */}
        {!isMobile ? (
          <div className="flex">
            <div
              className={`overflow-hidden border-l border-border/60 transition-[width,opacity] duration-200 ease-out ${
                panelCollapsed ? "w-0 opacity-0" : "w-[240px] opacity-100"
              }`}
              aria-hidden={panelCollapsed}
            >
              <NavSubPanel
                section={activeSection}
                activeTo={activeTo}
                onCollapse={togglePanel}
              />
            </div>
            <NavRail {...railProps} />
          </div>
        ) : null}

        <main className="min-h-dvh flex-1 p-4 md:p-8">
          <header className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {isMobile ? (
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      aria-label="فتح القائمة"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-foreground"
                    >
                      <Menu className="h-5 w-5" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="flex w-[320px] gap-0 p-0">
                    <NavSubPanel
                      section={activeSection}
                      activeTo={activeTo}
                      onNavigate={() => setMobileOpen(false)}
                    />
                    <NavRail {...railProps} />
                  </SheetContent>
                </Sheet>
              ) : (
                <button
                  type="button"
                  onClick={togglePanel}
                  aria-label={panelCollapsed ? "إظهار اللوحة الجانبية" : "طي اللوحة الجانبية"}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
                >
                  <PanelRightOpen className="h-5 w-5" />
                </button>
              )}
              {activeSection ? (
                <div className="hidden md:block">
                  <p className="kicker text-muted-foreground">{activeSection.label}</p>
                </div>
              ) : null}
            </div>
          </header>
          <Outlet />
        </main>
      </div>
    </div>
  );
}